import { useCurrentClientConfig } from '@src/hooks/useCurrentClientConfig';
import { IntlContext, SUPPORTED_LOCALES, usePreferenceState } from 'pe-core-frontend';
import { useContext, useMemo } from 'react';
const PREFERENCE_KEY = 'locale';
export const useUserLocale = (skipUserPreferences = false) => {
    const { setLocale: setIntlLocale } = useContext(IntlContext);
    const { clientConfig } = useCurrentClientConfig();
    // Use SUPPORTED_LOCALES constant instead of fetching from GraphQL
    const clientLocales = useMemo(() => SUPPORTED_LOCALES.map(locale => ({
        identifier: locale.locale,
        displayName: locale.name,
        englishName: locale.name,
        currencies: [],
    })), []);
    const defaultLocale = useMemo(() => clientConfig?.locale || '', [clientConfig?.locale]);
    const [preference, setPreference] = usePreferenceState(PREFERENCE_KEY, defaultLocale, skipUserPreferences);
    /**
     * Sets the user's locale preference and updates the locale accordingly.
     *
     * @param locale - The locale to set as the user's preference.
     */
    const setLocale = (locale) => {
        setPreference(locale);
        setIntlLocale((locale ?? '').replace('_', '-')); // Convert to formatjs format
    };
    /**
     * Retrieves the user's locale based on the provided preference and supported locales.
     * If the preference is a supported locale, it will be returned.
     * Otherwise, the default locale will be returned.
     *
     * @param preference - The user's locale preference.
     * @param clientLocales - An array of supported locales.
     * @param defaultLocale - The default locale to be used.
     * @returns The user's locale.
     */
    const userLocale = useMemo(() => {
        if (preference && clientLocales.some(l => l.identifier === preference)) {
            return preference;
        }
        return defaultLocale;
    }, [preference, clientLocales, defaultLocale]);
    return {
        locale: userLocale,
        setLocale,
        clientLocales,
    };
};
